import React, { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

export interface DayContext {
    isSelected: boolean;
    isToday: boolean;
    isAvailable: boolean;
    isoKey: string;
}

interface CalendarProps {
    selected: Date | null;
    onSelect: (date: Date) => void;
    minDate?: Date;
    availableIsoDates?: Set<string>;
    renderDay?: (day: number, date: Date, ctx: DayContext) => ReactNode;
}

const Calendar: React.FC<CalendarProps> = ({ selected, onSelect, minDate, availableIsoDates, renderDay }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const normalize = (d: Date) => {
        const c = new Date(d);
        c.setHours(0, 0, 0, 0);
        return c;
    };

    const effectiveMinDate = minDate ? normalize(minDate) : today;

    const toIso = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const isAvailable = (date: Date) => {
        const d = normalize(date);
        const inRange = !availableIsoDates || availableIsoDates.size === 0 || availableIsoDates.has(toIso(date));
        return inRange && d.getTime() >= effectiveMinDate.getTime();
    };

    const computeCtx = (date: Date): DayContext => ({
        isSelected: selected ? normalize(selected).getTime() === normalize(date).getTime() : false,
        isToday: normalize(today).getTime() === normalize(date).getTime(),
        isAvailable: isAvailable(date),
        isoKey: toIso(date),
    });

    const hasCustomRender = !!renderDay;

    return (
        <div className={hasCustomRender ? 'cal-custom-tiles' : 'cal-default-tiles'}>
            <ReactCalendar
                value={selected ?? undefined}
                onChange={(value) => {
                    if (value instanceof Date) onSelect(value);
                }}
                defaultView="month"
                minDetail="month"
                minDate={effectiveMinDate}
                locale="fr-FR"
                prevLabel="‹"
                nextLabel="›"
                prev2Label={null}
                next2Label={null}
                showNeighboringMonth={false}
                tileDisabled={({ date }) => !isAvailable(date)}
                tileContent={({ date, view }) => {
                    if (view !== 'month') return null;
                    const ctx = computeCtx(date);
                    const d = date.getDate();

                    if (renderDay) return renderDay(d, date, ctx);

                    return (
                        <div className="flex flex-col items-center justify-start h-full pt-0.5">
                            {!ctx.isAvailable ? (
                                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                                    <span className="text-sm font-bold text-gray-400 dark:text-gray-500">{d}</span>
                                </div>
                            ) : ctx.isSelected ? (
                                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-app-primary border-2 border-orange-700/60 shadow-md shadow-app-primary/20">
                                    <span className="text-sm font-bold text-white">{d}</span>
                                </div>
                            ) : (
                                <div
                                    className={cn(
                                        'w-7 h-7 flex items-center justify-center rounded-full border-2 bg-white dark:bg-gray-800',
                                        ctx.isToday ? 'border-app-primary' : 'border-app-primary/30 hover:border-app-primary'
                                    )}
                                >
                                    <span className="text-sm font-bold text-app-primary">{d}</span>
                                </div>
                            )}
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default Calendar;
